import { useEffect, useState } from "react";
import { Download, Mail, FileText, FileSpreadsheet, Package, Clock } from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";

function fmt(n) {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(n || 0);
}

async function downloadFile(path, params, filename) {
  const url = `${API_BASE}${path}?${new URLSearchParams(params).toString()}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function ExportRow({ label, onPDF, onCSV, onEmail, isAdmin }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="text-sm font-medium text-navy">{label}</div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onPDF} data-testid={`${label.toLowerCase()}-pdf-btn`}>
          <FileText className="w-3.5 h-3.5 mr-1" /> PDF
        </Button>
        <Button variant="outline" size="sm" onClick={onCSV} data-testid={`${label.toLowerCase()}-csv-btn`}>
          <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV
        </Button>
        {onEmail && isAdmin && (
          <Button size="sm" className="bg-gold hover:bg-gold-hover text-white" onClick={onEmail} data-testid={`${label.toLowerCase()}-email-btn`}>
            <Mail className="w-3.5 h-3.5 mr-1" /> Email
          </Button>
        )}
      </div>
    </div>
  );
}

function DailyTab() {
  const { isAdmin } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  const load = async (d = date) => {
    const { data } = await api.get("/reports/daily", { params: { date: d } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-5" data-testid="daily-report-tab">
      <Card className="section-card space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" data-testid="daily-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <Button onClick={() => load(date)} className="bg-navy text-white hover:bg-navy-hover" data-testid="daily-load-btn">
            Generate
          </Button>
        </div>
        <ExportRow
          label="Daily"
          isAdmin={isAdmin}
          onPDF={() => downloadFile("/reports/daily/export", { format: "pdf", date }, `daily_${date}.pdf`)}
          onCSV={() => downloadFile("/reports/daily/export", { format: "csv", date }, `daily_${date}.csv`)}
          onEmail={async () => {
            try {
              const { data } = await api.post(`/reports/daily/email?date=${date}`);
              if (data.sent) toast.success(`Emailed to ${data.recipient}`);
              else toast.warning(`Email not sent: ${data.reason || "configure RESEND_API_KEY"}`);
            } catch (err) { toast.error(formatApiError(err)); }
          }}
        />
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Total Sales" value={fmt(data.total_sales)} testid="daily-total" />
            <Kpi label="Transactions" value={data.sales_count} testid="daily-count" />
            <Kpi label="Subscription Revenue" value={fmt(data.subscription_sales)} testid="daily-subs" />
            <Kpi label="Stock Reduced" value={`${data.stock_reduced} units`} testid="daily-stock-reduced" />
          </div>
          {isAdmin && data.profit_estimate != null && (
            <Card className="section-card bg-gold/5 border-gold/30">
              <div className="text-xs uppercase tracking-wider text-gold font-semibold">Estimated profit (Admin only)</div>
              <div className="font-heading text-3xl font-semibold text-navy mt-1" data-testid="daily-profit">{fmt(data.profit_estimate)}</div>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="section-card">
              <h4 className="font-heading text-base font-semibold text-navy mb-3">By Payment Method</h4>
              <div className="space-y-2">
                {Object.entries(data.by_payment).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-medium text-navy">{fmt(v)}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="section-card">
              <h4 className="font-heading text-base font-semibold text-navy mb-3">Products Sold</h4>
              {data.products_sold.length === 0 ? (
                <div className="text-sm text-muted-foreground">No items sold this day.</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-auto">
                  {data.products_sold.map((p) => (
                    <div key={p.name} className="flex justify-between text-sm">
                      <span className="text-navy">{p.name}</span>
                      <Badge variant="outline">{p.qty}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function MonthlyTab() {
  const { isAdmin } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/reports/monthly", { params: { year, month } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-5" data-testid="monthly-report-tab">
      <Card className="section-card space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5">
            <Label>Year</Label>
            <Input type="number" data-testid="monthly-year-input" className="w-28" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label>Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-40" data-testid="monthly-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) =>
                  <SelectItem key={m} value={String(m)}>{new Date(2000, m - 1, 1).toLocaleString("en", { month: "long" })}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} className="bg-navy text-white hover:bg-navy-hover" data-testid="monthly-load-btn">Generate</Button>
        </div>
        <ExportRow
          label="Monthly"
          isAdmin={isAdmin}
          onPDF={() => downloadFile("/reports/monthly/export", { format: "pdf", year, month }, `monthly_${year}_${month}.pdf`)}
          onCSV={() => downloadFile("/reports/monthly/export", { format: "csv", year, month }, `monthly_${year}_${month}.csv`)}
          onEmail={async () => {
            try {
              const { data } = await api.post(`/reports/monthly/email?year=${year}&month=${month}`);
              if (data.sent) toast.success(`Emailed to ${data.recipient}`);
              else toast.warning(`Email not sent: ${data.reason || "configure RESEND_API_KEY"}`);
            } catch (err) { toast.error(formatApiError(err)); }
          }}
        />
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Revenue" value={fmt(data.total_revenue)} testid="monthly-revenue" />
            <Kpi label="Sales" value={data.sales_count} testid="monthly-count" />
            <Kpi label="Customers" value={data.unique_customers} testid="monthly-customers" />
            <Kpi label="Units Moved" value={data.inventory_units_moved} testid="monthly-units" />
          </div>
          {isAdmin && data.profit_estimate != null && (
            <Card className="section-card bg-gold/5 border-gold/30">
              <div className="text-xs uppercase tracking-wider text-gold font-semibold">Estimated profit (Admin only)</div>
              <div className="font-heading text-3xl font-semibold text-navy mt-1" data-testid="monthly-profit">{fmt(data.profit_estimate)}</div>
            </Card>
          )}
          <Card className="section-card">
            <h4 className="font-heading text-base font-semibold text-navy mb-3">Best Sellers</h4>
            {data.best_sellers.length === 0 ? (
              <div className="text-sm text-muted-foreground">No sales this month.</div>
            ) : (
              <ol className="space-y-2">
                {data.best_sellers.map((b, i) => (
                  <li key={b.name} className="flex justify-between text-sm">
                    <span><span className="text-gold font-semibold mr-2">{i + 1}.</span>{b.name}</span>
                    <Badge variant="outline">{b.qty}</Badge>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function StockTab() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/reports/stock").then(({ data }) => setData(data)); }, []);

  return (
    <div className="space-y-5" data-testid="stock-report-tab">
      <Card className="section-card">
        <ExportRow
          label="Stock"
          isAdmin={isAdmin}
          onPDF={() => downloadFile("/reports/stock/export", { format: "pdf" }, `stock_${new Date().toISOString().slice(0,10)}.pdf`)}
          onCSV={() => downloadFile("/reports/stock/export", { format: "csv" }, `stock_${new Date().toISOString().slice(0,10)}.csv`)}
        />
      </Card>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Total Items" value={data.products.length} testid="stock-total-items" />
            <Kpi label="Low Stock" value={data.low_stock.length} testid="stock-low" />
            <Kpi label="Out of Stock" value={data.out_of_stock.length} testid="stock-out" />
            <Kpi label="Retail Value" value={fmt(data.retail_value)} testid="stock-retail" />
          </div>
          {isAdmin && data.cost_value != null && (
            <Card className="section-card bg-gold/5 border-gold/30">
              <div className="text-xs uppercase tracking-wider text-gold font-semibold">Inventory cost value (Admin only)</div>
              <div className="font-heading text-3xl font-semibold text-navy mt-1" data-testid="stock-cost-value">{fmt(data.cost_value)}</div>
            </Card>
          )}
          <Card className="section-card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Product</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-right px-4 py-3">Qty</th>
                  <th className="text-right px-4 py-3">Sell</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.products.map((p) => (
                  <tr key={p.id} className="border-t border-border/60">
                    <td className="px-4 py-2.5 font-medium text-navy">{p.name}</td>
                    <td className="px-4 py-2.5">{p.category}</td>
                    <td className="px-4 py-2.5 text-right">{p.quantity}</td>
                    <td className="px-4 py-2.5 text-right">${p.selling_price.toFixed(2)}</td>
                    <td className="px-4 py-2.5">
                      <Badge className={p.status === "Out of Stock" ? "bg-destructive text-white" : p.status === "Low Stock" ? "bg-gold text-white" : "bg-emerald-100 text-emerald-700"}>
                        {p.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="font-heading text-2xl font-semibold text-navy mt-2">{value}</div>
    </div>
  );
}

function SchedulerBanner() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/scheduler/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  if (!status || !status.enabled) return null;

  const next = status.next_run_at ? new Date(status.next_run_at) : null;
  const last = status.last_run_at ? new Date(status.last_run_at) : null;
  return (
    <Card className="p-4 border-gold/40 bg-gold/5 flex items-center justify-between gap-3 flex-wrap" data-testid="scheduler-banner">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-gold/20 text-gold flex items-center justify-center">
          <Clock className="w-5 h-5" />
        </div>
        <div>
          <div className="text-sm font-semibold text-navy">
            Automatic end-of-day email is ON
          </div>
          <div className="text-xs text-muted-foreground">
            Daily report + stock report will be emailed to <b>{status.recipient}</b> at <b>{status.schedule}</b> ({status.timezone}).
            {next && <> Next run: <b>{next.toLocaleString()}</b>.</>}
            {last && <> Last run: {last.toLocaleString()}{status.last_result?.sent ? " ✓" : " (failed)"}.</>}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Reports() {
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-navy">Reports</h1>
        <p className="text-sm text-muted-foreground">Daily, monthly and stock — export PDF/CSV or email.</p>
      </div>
      <SchedulerBanner />
      <Tabs defaultValue="daily">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-4"><DailyTab /></TabsContent>
        <TabsContent value="monthly" className="mt-4"><MonthlyTab /></TabsContent>
        <TabsContent value="stock" className="mt-4"><StockTab /></TabsContent>
      </Tabs>
    </div>
  );
}
